import { useCallback, useRef, useState } from 'react';
import type { PointerEvent } from 'react';

interface UseDragScrubOptions {
  /** Total number of phases (4 to ~10 in practice). */
  phaseCount: number;
  /** The committed current index — the hook starts each drag from here. */
  currentIndex: number;
  /** Width of the drag surface in CSS pixels. Pass `containerRef.current?.clientWidth ?? 0`. */
  width: number;
  /** Fires on pointerup ONLY if the index actually changed. */
  onCommit: (newIndex: number) => void;
}

interface UseDragScrubReturn {
  isDragging: boolean;
  /** The live index during a drag — equals `currentIndex` when not dragging. */
  dragIndex: number;
  onPointerDown: (e: PointerEvent) => void;
  onPointerMove: (e: PointerEvent) => void;
  onPointerUp: (e: PointerEvent) => void;
  onPointerCancel: (e: PointerEvent) => void;
}

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

/** Pointer-driven drag handler. Maps horizontal pointer travel to a phase index.
 *  Full-width drag traverses `phaseCount - 1` indices in either direction.
 *  Returns a live `dragIndex` for in-flight rendering and fires `onCommit` on
 *  pointerup if the rounded index differs from the start. Pointercancel aborts
 *  the gesture without committing. */
export function useDragScrub(opts: UseDragScrubOptions): UseDragScrubReturn {
  const { phaseCount, currentIndex, width, onCommit } = opts;
  const [isDragging, setDragging] = useState(false);
  const [dragIndex, setDragIndex] = useState(currentIndex);
  const startXRef = useRef(0);
  const startIndexRef = useRef(currentIndex);

  // Keep dragIndex in sync with parent updates when not dragging.
  if (!isDragging && dragIndex !== currentIndex) {
    setDragIndex(currentIndex);
  }

  const span = Math.max(1, phaseCount - 1);

  const computeIndex = useCallback(
    (clientX: number): number => {
      if (width <= 0 || phaseCount <= 1) return startIndexRef.current;
      const delta = clientX - startXRef.current;
      const indexDelta = (delta / width) * span;
      const next = Math.round(startIndexRef.current + indexDelta);
      return clamp(next, 0, phaseCount - 1);
    },
    [width, phaseCount, span],
  );

  const onPointerDown = useCallback((e: PointerEvent) => {
    startXRef.current = e.clientX;
    startIndexRef.current = currentIndex;
    setDragIndex(currentIndex);
    setDragging(true);
    // Capture the pointer so move events keep firing if the finger leaves the element.
    const target = e.currentTarget as Element | null;
    if (target && 'setPointerCapture' in target) {
      try {
        (target as Element & { setPointerCapture: (id: number) => void }).setPointerCapture(e.pointerId);
      } catch {
        /* jsdom lacks pointer capture — ignore in tests. */
      }
    }
  }, [currentIndex]);

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      if (!isDragging) return;
      setDragIndex(computeIndex(e.clientX));
    },
    [isDragging, computeIndex],
  );

  const onPointerUp = useCallback(
    (e: PointerEvent) => {
      if (!isDragging) return;
      const finalIndex = computeIndex(e.clientX);
      setDragging(false);
      setDragIndex(finalIndex);
      if (finalIndex !== startIndexRef.current) {
        onCommit(finalIndex);
      }
    },
    [isDragging, computeIndex, onCommit],
  );

  const onPointerCancel = useCallback(
    (_e: PointerEvent) => {
      if (!isDragging) return;
      setDragging(false);
      setDragIndex(startIndexRef.current);
    },
    [isDragging],
  );

  return {
    isDragging,
    dragIndex,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
  };
}
